function psRollbar(context, err, extra) {
  if (!window.Rollbar) return;
  try {
    const message =
      err && typeof err === 'object' && err.message
        ? String(err.message)
        : typeof err === 'string'
          ? err
          : 'PredictiveSearch error';
    Rollbar.error(message, err instanceof Error ? err : { detail: err }, {
      context: 'PredictiveSearch.' + context,
      ...(extra && typeof extra === 'object' ? extra : {}),
    });
  } catch (_) {}
}

// utility.js can fail to load (network error, content blocker); fall back so search still works.
function psDebounce(fn, wait) {
  if (typeof Utility !== 'undefined' && typeof Utility.debounce === 'function') {
    return Utility.debounce(fn, wait);
  }
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    try {
      this.cachedResults = {};
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.searchTerm = '';
      this.setupEventListeners();
    } catch (e) {
      psRollbar('constructor', e);
    }
  }

  /**
   * Bind the events on the search input
   */
  setupEventListeners() {
    try {
      if (this.input) {
        this.input.addEventListener(
          'input',
          psDebounce((event) => {
            try {
              this.onChange(event);
            } catch (e) {
              psRollbar('setupEventListeners.input', e);
            }
          }, 500).bind(this)
        );
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }
    } catch (e) {
      psRollbar('setupEventListeners', e);
    }
  }

  /**
   *
   * @returns Search Query with trim value
   */
  getQuery() {
    try {
      return (this.input?.value || '').trim();
    } catch (e) {
      psRollbar('getQuery', e);
      return '';
    }
  }

  onChange() {
    try {
      const newSearchTerm = this.getQuery();

      if (!newSearchTerm || newSearchTerm.length <= 0) {
        this.hideResults();
        return;
      }

      this.searchTerm = newSearchTerm;
      this.getSearchResults(this.searchTerm);
    } catch (e) {
      psRollbar('onChange', e);
    }
  }

  /**
   * Trigger Form Submit event
   * @param {*} event
   */

  onFormSubmit(event) {
    try {
      event.preventDefault();
    } catch (e) {
      psRollbar('onFormSubmit', e);
    }
  }

  /**
   * Search term was changed from other search input, treat it as a user change
   */
  onFocus() {
    try {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    } catch (e) {
      psRollbar('onFocus', e);
    }
  }

  /**
   * Call Sections APIs for search results
   * @param {*} searchTerm
   */
  getSearchResults(searchTerm) {
    try {
      if (!this.predictiveSearchResults) {
        psRollbar('getSearchResults', new Error('predictiveSearchResults missing'));
        return;
      }
      const searchFields =
        'author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor';
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.predictiveSearchResults.setAttribute('loading', true);

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      const urlBase = window.routes?.predictive_searh || window.routes?.predictive_search;
      if (!urlBase) {
        this.predictiveSearchResults.setAttribute('loading', false);
        psRollbar('getSearchResults', new Error('predictive search route missing'));
        return;
      }

      fetch(
        `${urlBase}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=10&resources[options][fields]=${searchFields}&resources[limit_scope]=each&section_id=predictive-search`
      )
        .then((response) => {
          if (!response.ok) {
            const err = new Error('Predictive search HTTP ' + response.status);
            err.status = response.status;
            throw err;
          }
          this.predictiveSearchResults.setAttribute('loading', false);
          return response.text();
        })
        .then((text) => {
          try {
            const section = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-predictive-search');
            if (!section) {
              psRollbar('getSearchResults.parse', new Error('predictive-search section not in response'));
              this.predictiveSearchResults.setAttribute('loading', false);
              return;
            }
            const resultsMarkup = section.innerHTML;
            this.cachedResults[queryKey] = resultsMarkup;
            this.renderSearchResults(resultsMarkup);
          } catch (e) {
            psRollbar('getSearchResults.renderMarkup', e);
            this.predictiveSearchResults.setAttribute('loading', false);
          }
        })
        .catch((error) => {
          this.predictiveSearchResults.setAttribute('loading', false);
          if (error?.code === 20) {
            return;
          }
          psRollbar('getSearchResults.fetch', error, { searchTerm: String(searchTerm).substring(0, 80) });
        });
    } catch (e) {
      psRollbar('getSearchResults', e);
      try {
        this.predictiveSearchResults?.setAttribute('loading', false);
      } catch (_) {}
    }
  }

  /**
   * Render Search results
   * @param {*} resultsMarkup
   */
  renderSearchResults(resultsMarkup) {
    try {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.predictiveSearchResults.setAttribute('loading', false);
      this.showResults();
      this.prioritizeKeltecPartMatches();
      this.processOemMatches();
    } catch (e) {
      psRollbar('renderSearchResults', e);
    }
  }

  /**
   * Normalize part numbers for comparison (KC15-022 == KC15022)
   */
  normalizePartNumber(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\s\-_./\\#,]/g, '');
  }

  partNumbersEqual(a, b) {
    const normA = this.normalizePartNumber(a);
    const normB = this.normalizePartNumber(b);
    return Boolean(normA && normB && normA === normB);
  }

  cellMatchesPartNumber(cellText, searchTerm) {
    const normTerm = this.normalizePartNumber(searchTerm);
    if (!normTerm || !cellText) return false;

    const trimmed = String(cellText).trim();
    if (this.normalizePartNumber(trimmed) === normTerm) return true;

    return trimmed
      .split(/[\s,;/|]+/)
      .some((token) => this.normalizePartNumber(token) === normTerm);
  }

  productTitleOrSkuMatches(item, searchTerm) {
    const term = (searchTerm || '').trim();
    if (!item || !term) return false;

    const title = (item.getAttribute('data-title') || '').trim();
    const sku = (item.getAttribute('data-sku') || '').trim();
    const termLower = term.toLowerCase();

    if (title && (title.toLowerCase().includes(termLower) || this.partNumbersEqual(title, term))) {
      return true;
    }
    if (sku && (sku.toLowerCase().includes(termLower) || this.partNumbersEqual(sku, term))) {
      return true;
    }
    return false;
  }

  /**
   * Put KELTEC title/SKU matches first so header search matches cross-reference behavior
   */
  prioritizeKeltecPartMatches() {
    try {
      const list = this.predictiveSearchResults?.querySelector('#results-products');
      if (!list) return;

      const items = Array.from(list.querySelectorAll('.list-item[data-title]'));
      if (!items.length) return;

      const term = this.searchTerm || '';
      const maxVisible = 4;

      const ranked = items.slice().sort((a, b) => {
        const aExact = this.partNumbersEqual(a.getAttribute('data-title'), term) ||
          this.partNumbersEqual(a.getAttribute('data-sku'), term)
          ? 0
          : 1;
        const bExact = this.partNumbersEqual(b.getAttribute('data-title'), term) ||
          this.partNumbersEqual(b.getAttribute('data-sku'), term)
          ? 0
          : 1;
        if (aExact !== bExact) return aExact - bExact;

        const aMatch = this.productTitleOrSkuMatches(a, term) ? 0 : 1;
        const bMatch = this.productTitleOrSkuMatches(b, term) ? 0 : 1;
        return aMatch - bMatch;
      });

      ranked.forEach((item, index) => {
        list.appendChild(item);
        item.classList.toggle('d-none', index >= maxVisible);
      });
    } catch (e) {
      psRollbar('prioritizeKeltecPartMatches', e);
    }
  }

  /**
   * Parse product.description table and find OEM matches
   */
  processOemMatches() {
    try {
      if (!this.predictiveSearchResults) return;
      const productItems = this.predictiveSearchResults.querySelectorAll('[data-product-description]');
      const searchTerm = (this.searchTerm || '').trim();

      productItems.forEach((item) => {
        try {
          const descriptionHtml = item.getAttribute('data-product-description');
          const searchOem = item.querySelector('.search-oem');
          const manufacturerSpan = item.querySelector('[data-manufacturer]');
          const oemSpan = item.querySelector('[data-oem]');
          const productLinks = item.querySelectorAll('[data-product-link]');

          if (!descriptionHtml || !manufacturerSpan || !oemSpan) {
            if (searchOem) searchOem.style.display = 'none';
            return;
          }

          const parser = new DOMParser();
          const doc = parser.parseFromString(descriptionHtml, 'text/html');
          let rows = doc.querySelectorAll('table tbody tr');
          if (!rows.length) {
            rows = doc.querySelectorAll('table tr');
          }
          rows = Array.from(rows).filter((row) => row.querySelector('td'));

          if (!rows.length) {
            if (searchOem) searchOem.style.display = 'none';
            return;
          }

          let matchedRow = null;
          let matchedOemCell = '';

          if (searchTerm) {
            for (const row of rows) {
              const cells = Array.from(row.querySelectorAll('td')).map((cell) =>
                (cell.textContent || '').trim()
              );
              if (!cells.length) continue;

              const matchingCell = cells.find((value) => this.cellMatchesPartNumber(value, searchTerm));
              if (matchingCell) {
                matchedRow = row;
                matchedOemCell = matchingCell;
                break;
              }
            }
          }

          // Do not fall back to the first OEM row — KELTEC title matches should not show a wrong OEM badge
          if (!matchedRow) {
            if (searchOem) searchOem.style.display = 'none';
            return;
          }

          const cells = matchedRow.querySelectorAll('td');
          if (cells.length < 2) {
            if (searchOem) searchOem.style.display = 'none';
            return;
          }

          const manufacturer = (cells[0].textContent || '').trim();
          const oem = matchedOemCell || (cells[1].textContent || '').trim();

          manufacturerSpan.textContent = manufacturer;
          oemSpan.textContent = oem;
          if (searchOem) searchOem.style.display = '';

          if (manufacturer === '' && oem === '') {
            const matchText = item.querySelector('[data-match-text]');
            if (matchText) matchText.style.display = 'none';
          }

          productLinks.forEach((link) => {
            try {
              const href = link.getAttribute('href');
              if (href) {
                const newhref = `${href.split('?')[0]}?manufacturerdetail=${encodeURIComponent(
                  oem + ' ^ ' + manufacturer
                )}`;
                link.setAttribute('href', newhref);
              }
            } catch (e) {
              psRollbar('processOemMatches.productLink', e);
            }
          });
        } catch (e) {
          psRollbar('processOemMatches.item', e);
        }
      });
    } catch (e) {
      psRollbar('processOemMatches', e);
    }
  }

  /**
   * Show Search results container
   */
  showResults() {
    try {
      this.predictiveSearchResults?.classList.add('show-results');
    } catch (e) {
      psRollbar('showResults', e);
    }
  }

  /**
   * Hide Search results container
   */
  hideResults() {
    try {
      this.predictiveSearchResults?.classList.remove('show-results');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
    } catch (e) {
      psRollbar('hideResults', e);
    }
  }

  /**
   * Clear search input and hide results
   * Called when clear button is clicked or overlay is clicked
   */
  clearSearch() {
    try {
      if (this.input) {
        this.input.value = '';
      }
      const clearBtn = document.querySelector('.clear-btn');
      if (clearBtn) {
        clearBtn.style.display = 'none';
      }
      this.onChange();
    } catch (e) {
      psRollbar('clearSearch', e);
    }
  }
}

/**
 * Handle predictive search overlay clicks
 * The overlay is created via CSS ::after pseudo-element on .site-header
 * Clicks on the overlay area should close the search results
 * This is set up once for all predictive-search instances
 */
if (!window.predictiveSearchOverlayHandlerSetup) {
  document.addEventListener('click', (e) => {
    try {
      const resultsContainer = document.querySelector('.predictive-search--header.show-results');
      if (!resultsContainer) return;

      const searchWrapper = document.querySelector('.predictive-search-wrapper');
      const siteHeader = document.querySelector('.site-header');

      const isClickInsideSearchArea = searchWrapper && searchWrapper.contains(e.target);
      const isClickOnResults = resultsContainer && resultsContainer.contains(e.target);

      let isClickOnHeaderContent = false;
      if (siteHeader) {
        const headerRect = siteHeader.getBoundingClientRect();
        const clickY = e.clientY;
        const isWithinHeaderBounds = clickY >= headerRect.top && clickY <= headerRect.bottom;
        const isHeaderChild = siteHeader.contains(e.target);
        isClickOnHeaderContent = isWithinHeaderBounds && isHeaderChild;
      }

      if (!isClickInsideSearchArea && !isClickOnResults && !isClickOnHeaderContent) {
        const predictiveSearchElement = document.querySelector('predictive-search');
        if (predictiveSearchElement && typeof predictiveSearchElement.clearSearch === 'function') {
          predictiveSearchElement.clearSearch();
        }
      }
    } catch (err) {
      psRollbar('overlayClickHandler', err);
    }
  });
  window.predictiveSearchOverlayHandlerSetup = true;
}

customElements.define('predictive-search', PredictiveSearch);
